import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Button, Container, Grid, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { templateService, IncidentTypes } from '@/services';
import type { NotificationTemplate } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications';
import { DeleteConfirmationDialog, NewPageTitleBar } from '@/components';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable.tsx';
import { getMenuData } from '@core/data/menuData.ts';
import NotificationTemplatesEditor, {
    NotificationTemplatesEditorRef
} from './components/templates/NotificationTemplatesEditor';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { useUnsavedChangesGuard } from '@core/hooks/useUnsavedChangesGuard.ts';
import { DateTime } from 'luxon';
import { useRegisterDirtyGuard } from '@core/hooks/useRegisterDirtyGuard.ts';
import { UserApiResponse, userService } from '@/services/userService.ts';
import { useApiStore, useAuthStore } from '@core/store';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay.tsx';
import { TFunction } from 'i18next';
import { ActiveLicenses } from '@core/types';

export function resolveDefaultTemplateContent(template: NotificationTemplate, t: TFunction): NotificationTemplate {
    if (!template.isDefault || template.id) return template;

    const subject = t('defaultTemplates.subject');

    const body =
        t('defaultTemplates.greeting') +
        t(`defaultTemplates.body.${template.incidentType}`) +
        t('defaultTemplates.disclaimer');

    return { ...template, subject, body };
}

const INCIDENT_TYPE_DEFS = [
    { value: IncidentTypes.BarrierManipulation, license: null, label: 'BarrierManipulation' },
    { value: IncidentTypes.CrawlAlarm, license: null, label: 'CrawlAlarm' },
    { value: IncidentTypes.DeviceOffline, license: null, label: 'DeviceOffline' },
    { value: IncidentTypes.DeviceOnline, license: null, label: 'DeviceOnline' },
    { value: IncidentTypes.DoorClosed, license: null, label: 'DoorClosed' },
    { value: IncidentTypes.DoorOpened, license: null, label: 'DoorOpened' },
    { value: IncidentTypes.EmergencyButtonPressed, license: null, label: 'EmergencyButtonPressed' },
    { value: IncidentTypes.ExternalDoorStuckOpen, license: null, label: 'ExternalDoorStuckOpen' },
    { value: IncidentTypes.GateOpenedMainDirection, license: null, label: 'GateOpenedMainDirection' },
    { value: IncidentTypes.GateOpenedOppositeDirection, license: null, label: 'GateOpenedOppositeDirection' },
    { value: IncidentTypes.GeneralAlarm, license: null, label: 'GeneralAlarm' },
    { value: IncidentTypes.InvalidScan, license: null, label: 'InvalidScan' },
    { value: IncidentTypes.OperationModeChanged, license: ActiveLicenses.ShopAutomation, label: 'OperationModeChanged' },
    { value: IncidentTypes.PanicAlarm, license: null, label: 'PanicAlarm' },
    { value: IncidentTypes.ReverseAlarm, license: null, label: 'ReverseAlarm' },
    { value: IncidentTypes.Tailgating, license: null, label: 'Tailgating' },
    { value: IncidentTypes.ValidScan, license: null, label: 'ValidScan' }
] as const;

const NotificationTemplates: FCSecurePage = () => {
    const { t } = useTranslation();
    const { notifyErrorResponse, notifySuccess } = useNotifications();
    const editorRef = useRef<NotificationTemplatesEditorRef>(null);

    const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
    const [originalName, setOriginalName] = useState<string | undefined>(undefined);
    const [templateToDelete, setTemplateToDelete] = useState<NotificationTemplate | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const [users, setUsers] = useState<UserApiResponse[]>([]);
    const { operationModes, loadOperationModes } = useApiStore();
    const { hasLicense } = useAuthStore();

    const discardedRef = useRef(false);
    const editorSectionRef = useRef<HTMLDivElement>(null);

    const isDirty = useCallback(() => !discardedRef.current && !!editorRef.current?.isDirty, []);

    useEffect(() => {
        if (selectedTemplate) {
            editorSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [selectedTemplate]);

    const resolvedTemplates = useMemo(
        () => templates.map((tpl) => resolveDefaultTemplateContent(tpl, t)),
        [templates, t]
    );

    useEffect(() => {
        setSelectedTemplate((prev) => {
            if (!prev?.isDefault) return prev;
            if (editorRef.current?.isDirty) return prev;

            const raw = templates.find((x) => x.isDefault && x.incidentType === prev.incidentType);

            return raw ? structuredClone(resolveDefaultTemplateContent(raw, t)) : prev;
        });
    }, [t]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [templates, users] = await Promise.all([
                templateService.loadTemplates(),
                userService.getUsers(),
                loadOperationModes()
            ]);

            const filteredTemplates = templates.filter((tpl) => {
                const def = INCIDENT_TYPE_DEFS.find(d => d.value === tpl.incidentType);
                return !def?.license || hasLicense(def.license);
            });

            setTemplates(filteredTemplates);
            setUsers(users);
            setLastUpdate(DateTime.now());
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    }, [loadOperationModes, hasLicense]);

    const allowedIncidentTypes = useMemo(() => {
        return INCIDENT_TYPE_DEFS
            .filter((d) => !d.license || hasLicense(d.license))
            .map((d) => ({
                value: d.value,
                label: d.label
            }));
    }, [hasLicense]);

    const handleCloseEditor = useCallback(
        (shouldReload = false) => {
            discardedRef.current = true;
            setSelectedTemplate(null);
            setOriginalName(undefined);
            if (shouldReload) {
                loadData();
            }
        },
        [loadData]
    );

    const save = useCallback(async () => {
        const saved = await editorRef.current?.save();
        if (saved) {
            handleCloseEditor(true);
        }
        return !!saved;
    }, [handleCloseEditor]);

    const discard = useCallback(() => {
        handleCloseEditor(false);
    }, [handleCloseEditor]);

    const { guard } = useUnsavedChangesGuard({ isDirty, save });
    useRegisterDirtyGuard({ checkDirty: isDirty, save, discard });

    const trySelectTemplate = useCallback(
        (next: NotificationTemplate, nextOriginalName?: string) => {
            guard(() => {
                discardedRef.current = false;
                setSelectedTemplate(next);
                setOriginalName(nextOriginalName);
            });
        },
        [guard]
    );

    const handleAddNew = useCallback(() => {
        trySelectTemplate({
            name: '',
            subject: '',
            body: '',
            createDate: new Date().toISOString(),
            isActiveOnStoreOperationModes: []
        });
    }, [trySelectTemplate]);

    useEffect(() => {
        const unsubscribeReload = eventBus.on(EventTypes.WIDGETS_RELOAD, loadData);
        loadData();
        return () => {
            unsubscribeReload();
        };
    }, [loadData]);

    const handleEdit = useCallback(
        (template: NotificationTemplate) => {
            trySelectTemplate(structuredClone(template));
        },
        [trySelectTemplate]
    );

    const handleCancelEditor = useCallback(() => {
        guard(() => handleCloseEditor(false));
    }, [guard, handleCloseEditor]);

    const handleAskDelete = useCallback((template: NotificationTemplate) => {
        setTemplateToDelete(template);
        setDeleteDialogOpen(true);
    }, []);

    const handleDelete = useCallback(async () => {
        if (!templateToDelete?.id) return;
        setLoading(true);
        setDeleteDialogOpen(false);
        try {
            await templateService.deleteTemplate(templateToDelete.id);
            notifySuccess();
            setTemplateToDelete(null);
            handleCloseEditor(true);
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    }, [templateToDelete]);

    const handleCancelDelete = useCallback(() => {
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
    }, []);

    const handleRefresh = useCallback(async () => {
        eventBus.emit(EventTypes.WIDGETS_RELOAD);
    }, []);

    useRegisterRefresh(handleRefresh);

    const handleCopyTemplate = useCallback(
        (template: NotificationTemplate) => {
            const copy: NotificationTemplate = {
                ...structuredClone(template),
                id: undefined,
                createDate: new Date().toISOString()
            };
            trySelectTemplate(copy, template.name);
        },
        [trySelectTemplate]
    );

    const tableColumns: DataTableColumn<NotificationTemplate>[] = useMemo(
        () => [
            {
                text: t('name'),
                value: 'name',
                width: 300
            },
            {
                text: t('type'),
                value: 'incidentType',
                width: 200,
                render: (template) => t(template.incidentType || '')
            },
            {
                text: t('actions'),
                value: 'actions',
                width: 100,
                align: 'left',
                sortable: false,
                isAction: true,
                render: (template) => {
                    const isEditing = selectedTemplate?.isDefault
                        ? selectedTemplate?.incidentType === template.incidentType
                        : selectedTemplate?.id === template.id;

                    return (
                        <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap' }}>
                            <IconButton
                                size="small"
                                onClick={() => handleEdit(template)}
                                title={t('edit')}
                                className={isEditing ? 'selected' : undefined}
                            >
                                <i className="bi bi-pencil" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleCopyTemplate(template)} title={t('copy')}>
                                <i className="bi bi-copy" />
                            </IconButton>
                            <IconButton
                                size="small"
                                disabled={template.isDefault}
                                onClick={() => handleAskDelete(template)}
                                title={t('delete')}
                            >
                                <i className="bi bi-trash3" />
                            </IconButton>
                        </Box>
                    );
                }
            }
        ],
        [t, selectedTemplate, handleEdit, handleCopyTemplate, handleAskDelete]
    );

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="bi bi-chat-left-dots" />}
                title={t('notificationTemplates')}
                menus={getMenuData()}
                iconSize="20px"
                currentRoute={{ path: '/manage-organisation/notificationtemplates' }}
                t={t}
                lastUpdate={lastUpdate}
            />

            <Box className="pagetitlebar-actions">
                <Button
                    variant="text"
                    onClick={handleAddNew}
                    className="pagetitlebar-action-btn pagetitlebar-action-btn--default"
                >
                    <Box className="pagetitlebar-action-btn-icon">
                        <i className="bi bi-plus-lg" />
                    </Box>
                    <span>{t('addNotificationTemplate')}</span>
                </Button>
            </Box>
            <Container maxWidth={false} className="page-template-container">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <NewWcnTable
                            columns={tableColumns}
                            data={resolvedTemplates}
                            loading={loading}
                            noDataMessage={t('noData')}
                            size="small"
                        />
                    </Grid>

                    {selectedTemplate && (
                        <Grid item xs={12} ref={editorSectionRef}>
                            <NotificationTemplatesEditor
                                ref={editorRef}
                                template={selectedTemplate}
                                originalName={originalName}
                                onSave={() => handleCloseEditor(true)}
                                onCancel={handleCancelEditor}
                                onCopy={handleCopyTemplate}
                                onDelete={handleAskDelete}
                                users={users}
                                operationModes={operationModes}
                                incidentTypeOptions={allowedIncidentTypes}
                            />
                        </Grid>
                    )}
                </Grid>
            </Container>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                heading={t('deleteHeading')}
                message={t('deleteMessage', { item: t('template') })}
                onConfirm={handleDelete}
                onCancel={handleCancelDelete}
            />

            <LoadingOverlay open={loading} />
        </Box>
    );
};

export default NotificationTemplates;
